/* ============================================
   COMMENTARY CONTEXT BUILDER
   ============================================
   Builds adaptive context objects for the LLM.
   Context size varies by event priority.
   Phase-aware: uses PossessionPhaseTracker data
   for incremental, possession-based context.
   ============================================ */

import { state } from '../state.js'
import { getEventPriority } from '../events/event-mapping.js'
import { getChronoData } from '../ui/radar-chrono.js'

// Events that trigger a full phase summary (climactic moments)
const CLIMACTIC_EVENTS = [
  'Frappe cadrée', 'Frappe non cadrée', 'But', 'Penalty',
  'Corner', 'Coup franc', 'Coup franc dangereux'
]

/**
 * Build the context object to send to the LLM.
 * Context is adaptive: size varies based on event priority.
 *
 * @param {string} eventType - Type of event
 * @param {string} team - 'HOME', 'AWAY', or 'neutral'
 * @param {import('./possession-phase-tracker.js').PossessionPhaseTracker|null} phaseTracker
 * @returns {object} Context object for the LLM
 */
export function buildCommentaryContext(eventType, team, phaseTracker = null) {
  const { priority } = getEventPriority(eventType)

  // Base context (always included)
  const context = {
    event: {
      type: eventType,
      team: team,
      priority
    },
    match: buildMatchContext()
  }

  // Phase context (if tracker is available)
  if (phaseTracker) {
    const phaseCtx = buildPhaseContext(phaseTracker, eventType, priority)
    if (phaseCtx) context.phase = phaseCtx
  }

  // Player info (always when available)
  const player = buildPlayerContext()
  if (player) context.player = player

  // Recent events (P1+ only when no phase context)
  if (priority >= 1 && !context.phase) {
    context.recentEvents = buildRecentEvents()
  }

  // Stats (P1+ and idle)
  if (priority >= 1 || eventType === 'idle') {
    const stats = buildStatsContext()
    if (stats) context.stats = stats
  }

  // Trend (P1+ and idle)
  if (priority >= 1 || eventType === 'idle') {
    const trend = buildTrendContext()
    if (trend) context.trend = trend
  }

  // Odds (P1+)
  if (priority >= 1) {
    const odds = buildOddsContext()
    if (odds) context.odds = odds
  }

  return context
}

/**
 * Build a context for idle commentary (no specific event).
 */
export function buildIdleContext(phaseTracker = null) {
  const ctx = {
    event: {
      type: 'idle',
      team: 'neutral',
      priority: 0
    },
    match: buildMatchContext(),
    stats: buildStatsContext(),
    trend: buildTrendContext()
  }

  if (phaseTracker) {
    const phase = phaseTracker.getCurrentPhase()
    if (phase) {
      ctx.phase = {
        team: phase.team,
        currentZone: phase.currentZone,
        passCount: phase.passCount,
        durationSec: phaseTracker.getPhaseDurationSec()
      }
    }
  }

  return ctx
}

// ── Phase context builder ────────────────────────

/**
 * Build phase-aware context based on the event's role within the phase.
 * Context varies by event position:
 * - First event: recovery info + start context
 * - Mid-phase: progression info
 * - Climactic events: full phase summary
 */
function buildPhaseContext(tracker, eventType, priority) {
  const phase = tracker.getCurrentPhase()
  if (!phase) return null

  const eventCount = phase.events.length
  const durationSec = tracker.getPhaseDurationSec()

  const isClimax = priority === 2 || CLIMACTIC_EVENTS.includes(eventType)

  // Full phase summary for high-impact moments
  if (isClimax && eventCount > 1) {
    return {
      team: phase.team,
      recoveryType: phase.recoveryType,
      startZone: phase.startZone,
      currentZone: phase.currentZone,
      highestZone: phase.highestZone,
      passCount: phase.passCount,
      durationSec,
      eventCount,
      startPlayer: phase.startPlayer,
      lastPlayer: phase.lastPlayer,
      score: phase.score,
      previousPhase: buildArchivedPhaseSummary(tracker.getArchivedPhases())
    }
  }

  // First event in phase: focus on recovery context
  if (eventCount <= 1) {
    return {
      team: phase.team,
      recoveryType: phase.recoveryType,
      startZone: phase.startZone,
      startPlayer: phase.startPlayer,
      score: phase.score,
      isPhaseStart: true
    }
  }

  // Mid-phase event: progression context
  const prevEvent = phase.events[eventCount - 2]
  return {
    team: phase.team,
    currentZone: phase.currentZone,
    previousZone: prevEvent?.zone || phase.startZone,
    passCount: phase.passCount,
    durationSec,
    lastPlayer: phase.lastPlayer,
    previousPlayer: prevEvent?.player || null
  }
}

/**
 * Minimal summary of the most recent archived phase.
 */
function buildArchivedPhaseSummary(archivedPhases) {
  if (!archivedPhases || archivedPhases.length === 0) return null

  const prev = archivedPhases[archivedPhases.length - 1]
  return {
    team: prev.team,
    highestZone: prev.highestZone,
    passCount: prev.passCount,
    eventCount: prev.events.length,
    lastEventType: prev.events[prev.events.length - 1]?.type || null
  }
}

// ── Private builders ──────────────────────────────

function buildMatchContext() {
  const ctx = {
    homeTeam: 'Home',
    awayTeam: 'Away',
    score: state.lastScore || '0-0',
    minute: null,
    half: null,
    status: null,
    addedTime: state.addedTime || null
  }

  // Chrono (minute + phase)
  const chrono = getChronoData()
  if (chrono.minute > 0 || chrono.statusId > 1) {
    ctx.minute = chrono.minute
    ctx.half = chrono.phase
    ctx.status = chrono.statusId
    if (chrono.addedTime > 0) ctx.addedTime = chrono.addedTime
  }

  // Static detail
  if (state.staticDetail) {
    const sd = state.staticDetail
    if (sd.homeTeam) ctx.homeTeam = sd.homeTeam.name || sd.homeTeam.short_name || 'Home'
    if (sd.awayTeam) ctx.awayTeam = sd.awayTeam.name || sd.awayTeam.short_name || 'Away'
    if (sd.tournament) ctx.competition = sd.tournament.name
    if (sd.venue) {
      ctx.venue = sd.venue.name
      if (sd.venue.city) ctx.venueCity = sd.venue.city
      if (sd.venue.capacity) ctx.venueCapacity = sd.venue.capacity
    }
    if (sd.referee) ctx.referee = sd.referee
    if (sd.homeCoach?.name) ctx.homeCoach = sd.homeCoach.name
    if (sd.awayCoach?.name) ctx.awayCoach = sd.awayCoach.name
    if (sd.homeRank) ctx.homeRank = sd.homeRank
    if (sd.awayRank) ctx.awayRank = sd.awayRank
    if (sd.homeResult) ctx.homeForm = sd.homeResult
    if (sd.awayResult) ctx.awayForm = sd.awayResult
    if (sd.environment) {
      if (sd.environment.weather) ctx.weather = sd.environment.weather
      if (sd.environment.temperature) ctx.temperature = sd.environment.temperature
    }
  }

  // Score from last known state (fallback)
  if (state.currentMatch?.homeScores) {
    const hs = state.currentMatch.homeScores
    const as = state.currentMatch.awayScores
    if (hs && as) ctx.score = `${hs[0] || 0}-${as[0] || 0}`
  }

  return ctx
}

function buildPlayerContext() {
  if (!state.lastPlayerInfo?.playerName) return null

  const info = state.lastPlayerInfo
  const player = {
    name: info.playerName
  }

  // Enrich from playerMapping if available
  if (state.playerMapping && info.playerId) {
    const mapped = state.playerMapping[info.playerId]
    if (mapped) {
      player.name = mapped.name || mapped.shortName || info.playerName
      player.shirtNumber = mapped.shirtNumber
      player.position = mapped.position
      if (mapped.captain) player.captain = true
    }
  }

  return player
}

function buildStatsContext() {
  if (!state.lastStats) return null

  const stats = {}

  // state.lastStats is keyed by translated stat name (e.g. "Possession"),
  // each entry has { id, home, away }. Iterate by stat.id for stability.
  for (const [, stat] of Object.entries(state.lastStats)) {
    if (!stat || stat.id == null) continue
    switch (stat.id) {
      case 1:  stats.goals = { home: stat.home, away: stat.away }; break
      case 2:  stats.corners = { home: stat.home, away: stat.away }; break
      case 3:  stats.yellowCards = { home: stat.home, away: stat.away }; break
      case 4:  stats.redCards = { home: stat.home, away: stat.away }; break
      case 5:  stats.offsides = { home: stat.home, away: stat.away }; break
      case 6:  stats.freeKicks = { home: stat.home, away: stat.away }; break
      case 8:  stats.penalties = { home: stat.home, away: stat.away }; break
      case 9:  stats.substitutions = { home: stat.home, away: stat.away }; break
      case 21: stats.shotsOnTarget = { home: stat.home, away: stat.away }; break
      case 22: stats.shotsOffTarget = { home: stat.home, away: stat.away }; break
      case 23: stats.attacks = { home: stat.home, away: stat.away }; break
      case 24: stats.dangerousAttacks = { home: stat.home, away: stat.away }; break
      case 25: stats.possession = { home: stat.home, away: stat.away }; break
      case 37: stats.blockedShots = { home: stat.home, away: stat.away }; break
    }
  }

  return Object.keys(stats).length > 0 ? stats : null
}

function buildTrendContext() {
  if (!state.trendData) return null

  return {
    currentMomentum: state.trendData.currentMomentum || 0,
    description: state.trendData.description || null
  }
}

function buildOddsContext() {
  if (!state.oddsData) return null

  return {
    home: state.oddsData.home || null,
    draw: state.oddsData.draw || null,
    away: state.oddsData.away || null
  }
}

function buildRecentEvents() {
  const events = state.events || []
  return events
    .slice(-5)
    .map(e => ({
      type: e.type || e.eventType,
      team: e.team,
      minute: e.minute
    }))
}
